import type { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiUpdateUserBadgeResponseBody } from '~/lib/api/modules/UserBadges';
import type { IUseUpdateUserBadgeMutationValues } from '~/lib/api/modules/UserBadges/hooks/useUpdateUserBadgeMutation';
import type { IUserBadgeFormValues } from './UserBadgeForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useUpdateUserBadgeMutation } from '~/lib/api/modules/UserBadges/hooks/useUpdateUserBadgeMutation';
import { useUserBadgeQuery } from '~/lib/api/modules/UserBadges/hooks/useUserBadgeQuery';
import { UserBadgeForm } from './UserBadgeForm';

export type IUpdateUserBadgeFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiUpdateUserBadgeResponseBody,
    inputValues: IUseUpdateUserBadgeMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
  userBadgeId: string;
};

export const UpdateUserBadgeForm: React.FC<IUpdateUserBadgeFormProps> = (
  props,
) => {
  const { sx, onSuccess, cancelUrl, appId, userBadgeId } = props;
  const userBadgeQuery = useUserBadgeQuery({ appId, userBadgeId });
  const updateUserBadgeMutation = useUpdateUserBadgeMutation();
  const { t } = useTranslation();
  const userBadge = userBadgeQuery.data?.userBadge;

  const handleSubmit: SubmitHandler<IUserBadgeFormValues> = (values) => {
    if (updateUserBadgeMutation.isLoading) {
      return;
    }

    return updateUserBadgeMutation.mutateAsync(
      {
        appId,
        userBadgeId,
        body: {
          access: values.access!,
          color: values.color!,
          description: values.description!,
          isDefault: !!values.isDefault,
          management: values.management!,
          name: values.name!,
          privacyPolicyUrl: values.privacyPolicyUrl,
          productId: values.productId,
          subscriptionUrl: values.subscriptionUrl,
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Title>{t('UpdateUserBadgeForm.title')}</ContentBlock.Title>
      <ContentBlock.Suspense
        loading={userBadgeQuery.isLoading}
        error={userBadgeQuery.error}
      >
        {userBadge ? (
          <UserBadgeForm
            mode="edition"
            cancelUrl={cancelUrl}
            defaultValues={{
              access: userBadge.access,
              color: userBadge.color,
              description: userBadge.description,
              isDefault: userBadge.isDefault,
              management: userBadge.management,
              name: userBadge.name,
              privacyPolicyUrl: userBadge.privacyPolicyUrl,
              productId: userBadge.storeProductId ?? '',
              subscriptionUrl: userBadge.subscriptionUrl,
            }}
            onSubmit={handleSubmit}
            appId={appId}
          />
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
